import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "next-themes";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import AdminRoute from "@/components/AdminRoute";
import CustomDomainRouter, { useCustomDomain } from "@/components/CustomDomainRouter";
import ErrorBoundary from "@/components/ErrorBoundary";
import LoadingFallback from "@/components/LoadingFallback";
import ModuleRoute from "@/components/ModuleRoute";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { MemberViewProvider } from "@/contexts/MemberViewContext";
import { OrgProvider } from "@/contexts/OrgContext";
import { TierProvider } from "@/contexts/TierContext";
import { BillingAccessProvider } from "@/contexts/BillingAccessContext";
import { WalkthroughProvider } from "@/hooks/walkthroughs";
import { AIGuidanceProvider } from "@/contexts/AIGuidanceContext";
import AppLayout from "./components/AppLayout";

const CommandCenter = React.lazy(() => import("./pages/CommandCenter"));
const Members = React.lazy(() => import("./pages/Members"));
const MemberSuccess = React.lazy(() => import("./pages/MemberSuccess"));
const MemberDetail = React.lazy(() => import("./pages/MemberDetail"));
const Programs = React.lazy(() => import("./pages/Programs"));
const Events = React.lazy(() => import("./pages/Events"));
const Intelligence = React.lazy(() => import("./pages/Insights"));
const Retention = React.lazy(() => import("./pages/Retention"));
const Reports = React.lazy(() => import("./pages/Reports"));
const Admin = React.lazy(() => import("./pages/Admin"));
const Auth = React.lazy(() => import("./pages/Auth"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const Profile = React.lazy(() => import("./pages/Profile"));
const MasterAdmin = React.lazy(() => import("./pages/MasterAdmin"));
const Billing = React.lazy(() => import("./pages/Billing"));
const PlatformBilling = React.lazy(() => import("./pages/PlatformBilling"));
const PublicEventRegistration = React.lazy(() => import("./pages/PublicEventRegistration"));
const Communications = React.lazy(() => import("./pages/Communications"));
const CRM = React.lazy(() => import("./pages/CRM"));
const Website = React.lazy(() => import("./pages/Website"));
const PublicSite = React.lazy(() => import("./pages/PublicSite"));
const Committees = React.lazy(() => import("./pages/Committees"));
const Sponsorship = React.lazy(() => import("./pages/Sponsorship"));
const Companies = React.lazy(() => import("./pages/Companies"));
const Tiers = React.lazy(() => import("./pages/Tiers"));
const Staff = React.lazy(() => import("./pages/Staff"));
const AssociationAdmin = React.lazy(() => import("./pages/AssociationAdmin"));
const MemberPortalPreview = React.lazy(() => import("./pages/MemberPortalPreview"));
const PortalRoutes = React.lazy(() => import("./pages/portal/PortalRoutes"));
const Unsubscribe = React.lazy(() => import("./pages/Unsubscribe"));
const Learning = React.lazy(() => import("./pages/Learning"));
const VideoLibrary = React.lazy(() => import("./pages/VideoLibrary"));
const LearningHub = React.lazy(() => import("./pages/LearningHub"));
const Community = React.lazy(() => import("./pages/Community"));
const Support = React.lazy(() => import("./pages/Support"));
const Funnels = React.lazy(() => import("./pages/Funnels"));
const ResponseStudioPage = React.lazy(() => import("./pages/ResponseStudio"));
const PublicFunnel = React.lazy(() => import("./pages/PublicFunnel"));
const PublicJobDetail = React.lazy(() => import("./pages/PublicJobDetail"));
const PublicOfferDetail = React.lazy(() => import("./pages/PublicOfferDetail"));
const PublicMembershipDetail = React.lazy(() => import("./pages/PublicMembershipDetail"));
const EventFeedback = React.lazy(() => import("./pages/EventFeedback"));
const PublicEventCheckIn = React.lazy(() => import("./pages/PublicEventCheckIn"));
const SignatureEvents = React.lazy(() => import("./pages/SignatureEvents"));
const ProgrammingCalendar = React.lazy(() => import("./pages/ProgrammingCalendar"));
const Playbooks = React.lazy(() => import("./pages/Playbooks"));
const MarketingPage = React.lazy(() => import("./pages/MarketingPage"));
const PricingPage = React.lazy(() => import("./pages/PricingPage"));
const RequestDemo = React.lazy(() => import("./pages/RequestDemo"));
const DemoThankYou = React.lazy(() => import("./pages/DemoThankYou"));
const StartTrial = React.lazy(() => import("./pages/StartTrial"));
const TrialWelcome = React.lazy(() => import("./pages/TrialWelcome"));
const TrialConvert = React.lazy(() => import("./pages/TrialConvert"));
const LaunchPage = React.lazy(() => import("./pages/LaunchPage"));
const MeetingOps = React.lazy(() => import("./pages/MeetingOps"));
const OrgExchange = React.lazy(() => import("./pages/OrgExchange"));
const Locations = React.lazy(() => import("./pages/Locations"));

const queryClient = new QueryClient();

/**
 * Inner routing component that has access to CustomDomainContext.
 * On custom domains, "/" renders the org's public site.
 * Platform routes (/portal, /auth, /register, etc.) still work on any domain.
 */
const AppRoutes = () => {
  const { slug: customDomainSlug } = useCustomDomain();

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/auth" element={<Auth />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/register/:orgSlug/:slug" element={<PublicEventRegistration />} />
        <Route path="/site/:orgSlug" element={<PublicSite />} />
        <Route path="/jobs/:slug" element={<PublicJobDetail />} />
        <Route path="/offers/:slug" element={<PublicOfferDetail />} />
        <Route path="/membership/:slug" element={<PublicMembershipDetail />} />
        <Route path="/portal/*" element={<PortalRoutes />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/event-feedback" element={<EventFeedback />} />
        <Route path="/checkin/:orgId/:eventId" element={<PublicEventCheckIn />} />
        <Route
          path="/member-portal-preview/*"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <MemberPortalPreview />
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route path="/f/:slug" element={<PublicFunnel />} />
        <Route path="/f/:slug/:pageSlug" element={<PublicFunnel />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/request-demo" element={<RequestDemo />} />
        <Route path="/request-demo/thank-you" element={<DemoThankYou />} />
        <Route path="/start-trial" element={<StartTrial />} />
        <Route
          path="/trial-welcome"
          element={
            <ProtectedRoute>
              <TrialWelcome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/trial/convert"
          element={
            <ProtectedRoute>
              <TrialConvert />
            </ProtectedRoute>
          }
        />
        {/* On custom domains, root path serves the org's public site; otherwise show marketing page */}
        {customDomainSlug ? (
          <Route path="/" element={<PublicSite overrideSlug={customDomainSlug} />} />
        ) : (
          <Route path="/" element={<MarketingPage />} />
        )}

        <Route
          path="*"
          element={
            /* On custom domains, unknown paths also show the public site */
            customDomainSlug ? (
              <PublicSite overrideSlug={customDomainSlug} />
            ) : (
              <ProtectedRoute>
                <AppLayout>
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                      <Route path="/dashboard" element={<CommandCenter />} />
                      <Route path="/launch" element={<LaunchPage />} />
                      <Route
                        path="/companies"
                        element={
                          <ModuleRoute module="membership">
                            <Companies />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/member-success"
                        element={
                          <ModuleRoute module="membership">
                            <MemberSuccess />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/members"
                        element={
                          <ModuleRoute module="membership">
                            <Members />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/members/:id"
                        element={
                          <ModuleRoute module="membership">
                            <MemberDetail />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/programs"
                        element={
                          <ModuleRoute module="programs">
                            <Programs />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/retention"
                        element={
                          <ModuleRoute module="membership">
                            <Retention />
                          </ModuleRoute>
                        }
                      />
                      <Route path="/billing" element={<Billing />} />
                      <Route path="/plans-billing" element={<PlatformBilling />} />
                      <Route
                        path="/reports"
                        element={
                          <ModuleRoute module="executive_intelligence">
                            <Reports />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/events"
                        element={
                          <ModuleRoute module="core_events">
                            <Events />
                          </ModuleRoute>
                        }
                      />
                      <Route path="/chamber-events" element={<Navigate to="/events" replace />} />
                      <Route
                        path="/signature-events"
                        element={
                          <ModuleRoute module="core_events">
                            <SignatureEvents />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/programming-calendar"
                        element={
                          <ModuleRoute module="programming_calendar">
                            <ProgrammingCalendar />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/playbooks"
                        element={
                          <ModuleRoute module="programming_calendar">
                            <Playbooks />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/communications"
                        element={
                          <ModuleRoute module="communications_pro">
                            <Communications />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/website"
                        element={
                          <ModuleRoute module="website_pro">
                            <Website />
                          </ModuleRoute>
                        }
                      />
                      <Route path="/crm" element={<CRM />} />
                      <Route path="/committees" element={<Committees />} />
                      <Route path="/meeting-ops" element={<MeetingOps />} />
                      <Route path="/locations" element={<Locations />} />
                      <Route
                        path="/sponsorship"
                        element={
                          <ModuleRoute module="sponsors">
                            <Sponsorship />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/tiers"
                        element={
                          <ModuleRoute module="membership">
                            <Tiers />
                          </ModuleRoute>
                        }
                      />
                      <Route path="/staff" element={<Staff />} />
                      <Route
                        path="/learning"
                        element={
                          <ModuleRoute module="programs">
                            <Learning />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/video-library"
                        element={
                          <ModuleRoute module="video_library">
                            <VideoLibrary />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/learning-hub"
                        element={
                          <ModuleRoute module="video_library">
                            <LearningHub />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/community"
                        element={
                          <ModuleRoute module="community">
                            <Community />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/support"
                        element={
                          <ModuleRoute module="support_ops">
                            <Support />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/intelligence"
                        element={
                          <ModuleRoute module="executive_intelligence">
                            <Intelligence />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/insights"
                        element={
                          <ModuleRoute module="executive_intelligence">
                            <Intelligence />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/admin"
                        element={
                          <AdminRoute>
                            <Admin />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="/settings"
                        element={
                          <AdminRoute>
                            <Admin />
                          </AdminRoute>
                        }
                      />
                      <Route path="/profile" element={<Profile />} />
                      <Route
                        path="/master-admin/*"
                        element={
                          <AdminRoute>
                            <MasterAdmin />
                          </AdminRoute>
                        }
                      />
                      <Route path="/association" element={<AssociationAdmin />} />
                      <Route path="/exchange" element={<OrgExchange />} />
                      <Route
                        path="/funnels"
                        element={
                          <ModuleRoute module="campaign_funnels">
                            <Funnels />
                          </ModuleRoute>
                        }
                      />
                      <Route
                        path="/response-studio"
                        element={
                          <ModuleRoute module="response_studio">
                            <ResponseStudioPage />
                          </ModuleRoute>
                        }
                      />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </AppLayout>
              </ProtectedRoute>
            )
          }
        />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <OrgProvider>
              <MemberViewProvider>
                <TierProvider>
                  <BillingAccessProvider>
                    <WalkthroughProvider>
                      <AIGuidanceProvider>
                        <TooltipProvider>
                          <Toaster />
                          <Sonner />
                          <BrowserRouter>
                            <CustomDomainRouter>
                              <AppRoutes />
                            </CustomDomainRouter>
                          </BrowserRouter>
                        </TooltipProvider>
                      </AIGuidanceProvider>
                    </WalkthroughProvider>
                  </BillingAccessProvider>
                </TierProvider>
              </MemberViewProvider>
            </OrgProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
